import { useMemo } from 'react';
import { CalendarClock, ClipboardCheck, ListTodo, UserCog } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocalization } from '@/hooks/useLocalization';
import { useUserRole } from '@/hooks/useUserRole';
import { useMyDayData } from '@/hooks/useRoleHomeData';
import { getRoleDisplayName } from '@/lib/permissions';
import { dueWithin, isOverdue, myActionItems } from '@/lib/homeSummaries';
import { HomeActions, HomeEmpty, HomePanel, HomeRow, StatRow, StatTile } from './HomeFrame';

interface Props { onNavigate: (view: string, params?: Record<string, string>) => void; }

/** Home for the basic `user` role: my action items, what is due, recent meetings and my access. Heading from the shell. */
const MyDay = ({ onNavigate }: Props) => {
  const { t, tx, shortDate, ago } = useLocalization();
  const { role } = useUserRole();
  const d = useMyDayData();
  const now = useMemo(() => new Date(), []);

  const mine = useMemo(() => myActionItems(d.actions, d.userId, d.myName), [d.actions, d.userId, d.myName]);
  const soon = useMemo(() => dueWithin(mine, 7, now), [mine, now]);
  const overdue = useMemo(() => mine.filter((i) => isOverdue(i.due_date, now)), [mine, now]);

  if (d.isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label={t('Loading…')}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HomeActions>
        <Button variant="outline" onClick={() => onNavigate('reports')}>{t('Open reports')}</Button>
        <Button onClick={() => onNavigate('meetings')}>{t('Open meetings')}</Button>
      </HomeActions>

      <StatRow>
        <StatTile label={t('My action items')} icon={<ListTodo className="h-4 w-4" />} value={mine.length}
          hint={mine.length ? t('open and assigned to you') : t('Nothing is assigned to you.')} />
        <StatTile label={t('Overdue')} icon={<CalendarClock className="h-4 w-4" />} value={overdue.length}
          tone={overdue.length > 0 ? 'critical' : 'default'} hint={overdue.length ? t('past their due date') : t('Nothing is overdue.')} />
        <StatTile label={t('Due this week')} icon={<ClipboardCheck className="h-4 w-4" />} value={soon.length}
          tone={soon.length > 0 ? 'warning' : 'default'} hint={t('in the next 7 days')} />
        <StatTile label={t('Your role')} icon={<UserCog className="h-4 w-4" />} value={<span className="text-xl">{role ? tx(getRoleDisplayName(role)) : '—'}</span>}
          hint={d.latestRequest ? t('Role request: {0}', { 0: tx(d.latestRequest.status) }) : t('Request a role to access more features')}
          onClick={() => onNavigate('settings')} />
      </StatRow>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel className="lg:col-span-2" title={t('My action items')} hint={d.myName ? t('Assigned to you by account or by the name {0}.', { 0: d.myName }) : t('Assigned to your account.')} action={<Badge variant="outline">{mine.length}</Badge>}>
          {mine.length === 0 ? (
            <HomeEmpty>{d.actionsFailed ? t('Action items could not be loaded.') : t('Nothing is assigned to you.')}</HomeEmpty>
          ) : mine.slice(0, 8).map((item) => {
            const late = isOverdue(item.due_date, now);
            return (
              <div key={item.id} className="flex min-h-11 items-center gap-3 rounded-lg border border-border p-3">
                <ListTodo className={late ? 'h-4 w-4 shrink-0 text-destructive' : 'h-4 w-4 shrink-0 text-primary'} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{item.title}</span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {item.due_date ? (late ? t('overdue since {0}', { 0: shortDate(item.due_date) }) : t('due {0}', { 0: shortDate(item.due_date) })) : t('No due date')}
                  </span>
                </span>
                {item.status && <Badge variant="outline" className="shrink-0">{tx(item.status)}</Badge>}
              </div>
            );
          })}
        </HomePanel>

        <HomePanel title={t('Recent meetings')}>
          {d.meetingsFailed ? (
            <HomeEmpty>{t('Meetings could not be loaded.')}</HomeEmpty>
          ) : d.meetings.length === 0 ? (
            <HomeEmpty>{t('No meetings are visible to your role yet.')}</HomeEmpty>
          ) : d.meetings.map((m) => (
            <HomeRow key={m.id} onClick={() => onNavigate('meetings')}>
              <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{m.title ?? t('Untitled')}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{ago(m.start_time)}</span>
              </span>
            </HomeRow>
          ))}
        </HomePanel>
      </div>
    </div>
  );
};

export default MyDay;
